import { useState, useEffect, useCallback } from "react";
import { getDecodeContent, publishDecodeContent, getDecodeHormones } from "../../../api/decode";
import type { DecodeCmsContent, DecodeHormoneReference, HormoneKey } from "../../../types/decode";
import { IconHand } from "../../../components/icons/IconHand";
import { IconDocument } from "../../../components/icons/IconDocument";
import { IconMolecule } from "../../../components/icons/IconMolecule";
import { HormoneCard } from "./HormoneCard";
import iconCheckCircle from "../../../assets/icon-check-circle.svg";

const KNOWN_HORMONES: HormoneKey[] = ["fsh", "lh", "estradiol", "progesterone", "amh", "prolactin", "tsh"];

const EMPTY_EXPLANATIONS = Object.fromEntries(
  KNOWN_HORMONES.map((h) => [h, ""]),
) as Record<HormoneKey, string>;

const EMPTY_CONTENT: DecodeCmsContent = {
  greeting: "",
  cycle_context_day3: "",
  cycle_context_day21: "",
  non_cycle_context: "",
  hormone_explanations: { ...EMPTY_EXPLANATIONS },
};

const TEXT_FIELDS: { key: keyof Omit<DecodeCmsContent, "hormone_explanations">; label: string; section: string }[] = [
  { key: "greeting", label: "Greeting Message", section: "greeting" },
  { key: "cycle_context_day3", label: "Cycle Context for Day 3 (Follicular Phase)", section: "cycle" },
  { key: "cycle_context_day21", label: "Cycle Context for Day 21 (Luteal Phase)", section: "cycle" },
  { key: "non_cycle_context", label: "Non-cycle Context", section: "cycle" },
];

export function HiyaDecodePage() {
  const [content, setContent] = useState<DecodeCmsContent>(EMPTY_CONTENT);
  const [originalContent, setOriginalContent] = useState<DecodeCmsContent>(EMPTY_CONTENT);
  const [hormones, setHormones] = useState<DecodeHormoneReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const isDirty = JSON.stringify(content) !== JSON.stringify(originalContent);

  const errors: Record<string, string> = {};
  for (const field of TEXT_FIELDS) {
    if (!content[field.key].trim()) {
      errors[field.key] = "This field is required";
    }
  }
  for (const key of KNOWN_HORMONES) {
    if (!content.hormone_explanations[key].trim()) {
      errors[`explanation-${key}`] = "This field is required";
    }
  }

  const isValid = Object.keys(errors).length === 0;
  const canPublish = isDirty && isValid && !saving;

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [cmsRes, hormonesRes] = await Promise.allSettled([
          getDecodeContent(),
          getDecodeHormones(),
        ]);

        if (cancelled) return;

        if (cmsRes.status === "fulfilled") {
          setContent(cmsRes.value.content);
          setOriginalContent(cmsRes.value.content);
        }

        if (hormonesRes.status === "fulfilled") {
          setHormones(hormonesRes.value);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!toastVisible) return;
    const timer = setTimeout(() => setToastVisible(false), 3000);
    return () => clearTimeout(timer);
  }, [toastVisible]);

  useEffect(() => {
    if (!errorMessage) return;
    const timer = setTimeout(() => setErrorMessage(""), 5000);
    return () => clearTimeout(timer);
  }, [errorMessage]);

  const handleFieldChange = useCallback(
    (key: keyof Omit<DecodeCmsContent, "hormone_explanations">, value: string) => {
      setContent((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleExplanationChange = useCallback(
    (hormoneKey: HormoneKey, value: string) => {
      setContent((prev) => ({
        ...prev,
        hormone_explanations: { ...prev.hormone_explanations, [hormoneKey]: value },
      }));
    },
    [],
  );

  const handleFieldBlur = useCallback((key: string) => {
    setTouched((prev) => new Set(prev).add(key));
  }, []);

  async function handlePublish() {
    if (!canPublish) return;
    setSaving(true);
    setErrorMessage("");
    try {
      const res = await publishDecodeContent(content);
      setContent(res.content);
      setOriginalContent(res.content);
      setTouched(new Set());
      setToastVisible(true);
    } catch {
      setErrorMessage("Failed to publish. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-text-secondary text-base">Loading...</span>
      </div>
    );
  }

  const greetingField = TEXT_FIELDS[0]!;
  const cycleFields = TEXT_FIELDS.filter((f) => f.section === "cycle");

  return (
    <div className="flex flex-col h-[calc(100vh-110px)]">
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Greeting Message Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <IconHand className="text-text-primary" />
            <h2 className="text-base font-semibold text-text-primary" id="greeting-heading">Greeting Message</h2>
          </div>
          <textarea
            id="field-greeting"
            aria-labelledby="greeting-heading"
            data-field={greetingField.key}
            autoFocus
            className={`w-full border rounded-2xl p-4 text-sm leading-relaxed text-text-primary bg-bg-primary resize-none focus:outline-none min-h-[221px] ${
              touched.has(greetingField.key) && errors[greetingField.key]
                ? "border-input-stroke-error"
                : "border-stroke-primary focus:border-brand-brown"
            }`}
            value={content[greetingField.key]}
            onChange={(e) => handleFieldChange(greetingField.key, e.target.value)}
            onBlur={() => handleFieldBlur(greetingField.key)}
            placeholder="Enter greeting message..."
          />
          {touched.has(greetingField.key) && errors[greetingField.key] && (
            <p className="text-input-hint-error text-sm mt-1">{errors[greetingField.key]}</p>
          )}
        </section>

        <hr className="border-stroke-primary my-6" />

        {/* Cycle Context Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <IconDocument className="text-text-primary" />
            <h2 className="text-base font-semibold text-text-primary">Cycle Context</h2>
          </div>
          <div className="flex flex-col gap-5">
            {cycleFields.map((field) => (
              <div key={field.key}>
                <label
                  htmlFor={`field-${field.key}`}
                  className="text-sm font-medium text-text-primary mb-2 block"
                >
                  {field.label}
                </label>
                <textarea
                  id={`field-${field.key}`}
                  data-field={field.key}
                  className={`w-full border rounded-2xl p-4 text-sm leading-relaxed text-text-primary bg-bg-primary resize-none focus:outline-none min-h-[74px] ${
                    touched.has(field.key) && errors[field.key]
                      ? "border-input-stroke-error"
                      : "border-stroke-primary focus:border-brand-brown"
                  }`}
                  value={content[field.key]}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  onBlur={() => handleFieldBlur(field.key)}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                />
                {touched.has(field.key) && errors[field.key] && (
                  <p className="text-input-hint-error text-sm mt-1">{errors[field.key]}</p>
                )}
              </div>
            ))}
          </div>
        </section>

        <hr className="border-stroke-primary my-6" />

        {/* Hormone Results Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <IconMolecule className="text-text-primary" />
            <h2 className="text-base font-semibold text-text-primary">Hormone Results</h2>
          </div>
          <div className="flex flex-col gap-5">
            {hormones.map((hormone, index) => {
              const hKey = hormone.hormone;
              const explanationKey = `explanation-${hKey}`;
              return (
                <div key={hormone.id}>
                  <HormoneCard
                    hormone={hormone}
                    explanation={content.hormone_explanations[hKey] ?? ""}
                    explanationError={errors[explanationKey]}
                    explanationTouched={touched.has(explanationKey)}
                    onExplanationChange={(val) => handleExplanationChange(hKey, val)}
                    onExplanationBlur={() => handleFieldBlur(explanationKey)}
                  />
                  {index < hormones.length - 1 && (
                    <hr className="border-stroke-primary mt-5" />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Button Bar */}
      <div className="border-t border-stroke-primary pt-5 flex justify-end -mx-6 px-6 shrink-0">
        <button
          onClick={handlePublish}
          disabled={!canPublish}
          className={`h-[51px] rounded-2xl px-6 font-semibold text-base text-text-btn transition-colors ${
            canPublish
              ? "bg-btn-primary cursor-pointer"
              : "bg-btn-primary-disabled cursor-not-allowed"
          }`}
        >
          {saving ? "Publishing..." : "Save changes and publish"}
        </button>
      </div>

      {/* Success Toast */}
      {toastVisible && (
        <output
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-bg-primary rounded-2xl shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] px-4 py-3 flex items-center gap-3"
        >
          <img src={iconCheckCircle} alt="" width={32} height={32} />
          <span className="text-sm font-medium leading-relaxed text-text-primary whitespace-nowrap">
            Changes saved and published
          </span>
        </output>
      )}

      {/* Error Toast */}
      {errorMessage && (
        <output
          role="alert"
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-bg-primary border border-input-stroke-error rounded-2xl shadow-[0px_0px_10px_0px_rgba(0,0,0,0.1)] px-4 py-3 flex items-center gap-3"
        >
          <span className="text-sm font-medium leading-relaxed text-system-red whitespace-nowrap">
            {errorMessage}
          </span>
        </output>
      )}
    </div>
  );
}
